import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { establishCurrentUser } from "./reducers/auth";
import { isServer } from "../store";
import SvgIcons from "./utils/SvgIcons/SvgIcons";
import NavBar from "app/components/organisms/NavBar/NavBar";
import Routes from "app/routes/Routes";
import getHubStatus from "app/utils/app-record-management/getHubStatus";
import { AppRecordsProvider } from "./components/organisms/InteractiveConfigToolWithAppRecords/AppRecordsContext";
import { useSocket } from "./hooks/useSocket";
import NotificationManager from "app/components/organisms/NotificationManager/NotificationManager";
import "./App.scss";

const MAINTENANCE_MODE_DESCRIPTION = [
  "Sorry the HUB is down for scheduled maintenance right now. For any urgent job please contact editorial graphics team."
];
const MAINTENANCE_MODE_DESCRIPTION_ADMIN = [
  "The site is currently in maintenance mode. The site is only visible to site administrators."
];

const isDevelopement = process.env.NODE_ENV === "development";

const App = () => {
  const dispatch = useDispatch();
  const user = useSelector(state => state.auth.user);
  const authenticated = useSelector(state => state.auth.authenticated);
  const [hubStatus, setHubStatus] = useState(null);

  // Use socket hook
  const socket = useSocket(user);

  useEffect(() => {
    if (!isServer) {
      dispatch(establishCurrentUser());
    }

    if (!isDevelopement) {
      fetchHubStatus();
    }
  }, [dispatch]);

  // Session checker - verifies session when user resumes activity after inactivity
  useEffect(() => {
    if (!authenticated || isServer) return;

    let lastActivity = Date.now();
    let isChecking = false;

    // Check session validity when user becomes active after inactivity
    const checkSessionOnActivity = async () => {
      const inactiveTime = Date.now() - lastActivity;
      
      // If user was inactive for more than 2 minutes, check session
      if (inactiveTime > 2 * 60 * 1000 && !isChecking) {
        isChecking = true;
        
        try {
          const response = await fetch('/api/shared/get-current-user', {
            credentials: 'include'
          });
          
          // Check if session is still valid
          if (response.ok) {
            const data = await response.json();
            
            // If not authenticated, redirect to login
            if (!data.authenticated || !data.user) {
              console.warn('[SESSION] Session expired. Redirecting to login...');
              const returnTo = window.location.pathname + window.location.search;
              window.location.href = `/auth/google?returnTo=${encodeURIComponent(returnTo)}`;
              return; // Stop further execution 
            }
          } else if (response.status === 401) {
            // Also handle explicit 401 responses
            console.warn('[SESSION] Session expired (401). Redirecting to login...');
            const returnTo = window.location.pathname + window.location.search;
            window.location.href = `/auth/google?returnTo=${encodeURIComponent(returnTo)}`;
            return;
          }
        } catch (error) {
          console.error('[SESSION] Error checking session:', error);
        } finally {
          isChecking = false;
        }
      }
      
      // Update last activity time
      lastActivity = Date.now();
    };

    const activityEvents = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    activityEvents.forEach(event => {
      document.addEventListener(event, checkSessionOnActivity, true);
    });

    // Cleanup
    return () => {
      activityEvents.forEach(event => {
        document.removeEventListener(event, checkSessionOnActivity, true);
      });
    };
  }, [authenticated]);

  const fetchHubStatus = async () => {
    try {
      const hubStatus = await getHubStatus();
      setHubStatus(hubStatus);
    } catch (error) {
      console.error(`Error:`, error);
    }
  };

  const userExists = typeof user !== "undefined";
  if (!hubStatus && !isDevelopement) return null;

  let isMaintenance = false, userGroup = "";
  if (hubStatus) {
    isMaintenance = hubStatus.status === "maintenance" ? hubStatus.value === "on" : isMaintenance;
  }

  if (userExists) {
    isMaintenance = isMaintenance && user.group === "admin_user" ? false : isMaintenance;
    userGroup = user.group;
  }

  return (
    <div id="app">
      <SvgIcons carat cross warning crop account />
      <NavBar
        authenticated={authenticated}
        currentPath={location.pathname}
        isBeta={true}
        user={user}
      />

      {isMaintenance && !isDevelopement && userGroup === "admin_user" ? (
        <p className="maintenance-admin">
          {MAINTENANCE_MODE_DESCRIPTION_ADMIN}
        </p>
      ) : (
        ""
      )}

      {isMaintenance && !isDevelopement ? (
        <div className="maintenance">{MAINTENANCE_MODE_DESCRIPTION}</div>
      ) : (
        <div id="content">
          <AppRecordsProvider socket={socket}>
            <NotificationManager />
            <Routes />
          </AppRecordsProvider>
        </div>
      )}
    </div>
  );
};

export default App;
